import { create } from 'zustand';
import { IUser, IUserStatistics } from '@/utils/types/User';
import { ApiRequest } from '@/utils/axios';
import { defaultUser } from '@/utils/constants';
import { createJSONStorage, persist } from 'zustand/middleware';
import SteamID from 'steamid';

export interface userStoreState {
    isAuthed: boolean;
    isFetched: boolean;
    user: IUser;
    sounds: boolean;
    snow: boolean;
    authv2: boolean;
    sitename: string;
    statistics: IUserStatistics;
    fetchUser: () => Promise<void>;
    patchUser: (patch: Partial<IUser>) => void;
    tradeLink: () => string;
}

const useUserStore = create<userStoreState>()(
    persist(
        (set, get) => ({
            isAuthed: false,
            isFetched: false,
            user: defaultUser,
            sounds: true,
            snow: false,
            authv2: false,
            sitename: '',
            statistics: {} as IUserStatistics,
            fetchUser: async () => {
                try {
                    const res: {
                        data: {
                            success: boolean;
                            response: { user: IUser; statistics: IUserStatistics };
                        };
                    } = await ApiRequest.get('/user', {});

                    // Apply the user update first — if /settings/list below
                    // fails (malformed response, network blip, etc.) we still
                    // want the fresh experience/balance to land in the store.
                    if (res?.data.success) {
                        set(() => ({
                            user: res.data.response.user,
                            statistics: res.data.response.statistics,
                            isAuthed: true,
                        }));
                    } else {
                        set(() => ({
                            user: defaultUser,
                            statistics: {} as IUserStatistics,
                            isAuthed: false,
                        }));
                    }
                } catch (err) {
                    console.log(err);
                }

                // Settings fetch is best-effort and isolated so its failure
                // can't drop the user update above.
                try {
                    const settings = await ApiRequest.get('/settings/list');
                    // Settings come back as strings ("0" / "1"). A non-empty
                    // string like "0" is truthy, so parse it explicitly instead
                    // of relying on Boolean(snow). Always set so toggling off works.
                    const raw = settings?.data?.response?.global?.snow;
                    const snowOn =
                        raw === true ||
                        Number(raw) > 0 ||
                        String(raw).toLowerCase() === 'true';
                    const sitename = settings?.data?.response?.global?.sitename ?? '';
                    set(() => ({ snow: snowOn, sitename: String(sitename) }));
                } catch {
                    /* non-critical */
                }

                // Auth method resolution is best-effort too — a failure just
                // leaves the default (legacy /api/auth) flow in place.
                try {
                    const resolve = await ApiRequest.get('/auth/resolve');
                    const method =
                        resolve?.data?.response?.method ?? resolve?.data?.method;
                    set(() => ({ authv2: method === 'authv2' }));
                } catch {
                    /* non-critical */
                }
            },
            patchUser: (patch) => {
                set((state) => ({ user: { ...state.user, ...patch } }));
            },
            tradeLink: () => {
                const user = get().user;

                const { accountid: steamid32 } = new SteamID(user?.steamid);

                if (user.tradeToken) {
                    return `https://steamcommunity.com/tradeoffer/new/?partner=${steamid32}&token=${user.tradeToken}`;
                }
                return '';
            },
        }),
        {
            name: 'user-storage', // name of the item in the storage (must be unique)
            storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
            // `snow` is server-controlled (settings/list) — never persist it, or a
            // stale cached value keeps the snow on regardless of the setting.
            partialize: (state) => ({
                user: state.user,
                isAuthed: state.isAuthed,
                isFetched: state.isFetched,
                sounds: state.sounds,
                statistics: state.statistics,
            }),
        }
    )
);

export default useUserStore;
